import { useState, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { IntegrationExplorer } from "./IntegrationExplorer";
import {
  DriveLogo,
  SlackLogo,
  MicrosoftLogo,
  QuickBooksLogo,
  StripeLogo,
  GmailLogo,
  CalendarLogo,
  SalesforceLogo,
} from "./integration-logos";

const ORBITAL_TO_CATALOG = {
  "google-drive": "google-drive",
  slack: "slack",
  "microsoft-365": "microsoft-365",
  crm: "salesforce",
  quickbooks: "quickbooks",
  calendar: "google-calendar",
  email: "gmail",
  stripe: "stripe",
};

const NODES = [
  { id: "google-drive", name: "Google Drive", Logo: DriveLogo, x: 47, y: 8 },
  { id: "slack", name: "Slack", Logo: SlackLogo, x: 25, y: 21 },
  { id: "microsoft-365", name: "Microsoft 365", Logo: MicrosoftLogo, x: 78, y: 15 },
  { id: "crm", name: "CRM", Logo: SalesforceLogo, x: 12, y: 48 },
  { id: "quickbooks", name: "QuickBooks", Logo: QuickBooksLogo, x: 85, y: 48 },
  { id: "calendar", name: "Calendar", Logo: CalendarLogo, x: 26, y: 79 },
  { id: "email", name: "Email", Logo: GmailLogo, x: 48, y: 87 },
  { id: "stripe", name: "Stripe", Logo: StripeLogo, x: 76, y: 75 },
];

const HUB = { x: 50, y: 47 };

const IntegrationCard = ({ node, hovered, setHovered, onOpen, compact }) => (
  <button
    type="button"
    data-testid={`integration-card-${node.id}`}
    aria-label={`Explore ${node.name} integration`}
    onClick={() => onOpen(ORBITAL_TO_CATALOG[node.id])}
    onMouseEnter={() => setHovered(node.id)}
    onMouseLeave={() => setHovered(null)}
    className={`bg-white rounded-2xl border flex flex-col items-center justify-center gap-2 cursor-pointer transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563FF] focus-visible:ring-offset-2 ${
      hovered === node.id
        ? "border-[#93C5FD] shadow-[0_14px_34px_-8px_rgb(10,25,47,0.18)]"
        : "border-slate-200/80 shadow-[0_6px_20px_rgb(10,25,47,0.06)]"
    } ${compact ? "p-4 w-full aspect-square" : "p-4 w-24 xl:w-28 aspect-square absolute -translate-x-1/2 -translate-y-1/2 z-10"}`}
    style={compact ? undefined : { left: `${node.x}%`, top: `${node.y}%` }}
  >
    <node.Logo />
    <span className="text-xs font-semibold text-nova-navy whitespace-nowrap">{node.name}</span>
  </button>
);

export const Integrations = () => {
  const [hovered, setHovered] = useState(null);
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const ctaRef = useRef(null);

  const openExplorer = (id = null) => {
    setSelectedId(id);
    setExplorerOpen(true);
  };

  const closeExplorer = () => {
    setExplorerOpen(false);
    setSelectedId(null);
    setTimeout(() => ctaRef.current?.focus(), 350);
  };

  return (
    <section id="integrations" data-testid="integrations-section" className="scroll-mt-36 bg-white overflow-x-clip">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,8fr)] gap-14 lg:gap-8 items-center">
          <Reveal>
            <div>
              <div className="flex items-stretch gap-6">
                <div>
                  <span
                    data-testid="chapter-05-number"
                    className="text-5xl lg:text-6xl font-extrabold tracking-tighter text-nova-blue leading-none"
                  >
                    05
                  </span>
                  <span className="block mt-3 text-[11px] font-bold tracking-[0.28em] uppercase text-nova-muted leading-relaxed">
                    Nova Gets It
                    <br />
                    Done
                  </span>
                </div>
                <span className="w-px bg-slate-200 self-stretch" aria-hidden="true" />
              </div>

              <p className="mt-12 text-[11px] font-bold tracking-[0.3em] uppercase text-nova-blue">
                Integrations
              </p>
              <h2 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.06] text-nova-navy">
                Works With
                <br />
                Your <span className="text-nova-blue">Existing Stack.</span>
              </h2>
              <p className="mt-5 text-base text-nova-slate leading-relaxed max-w-sm">
                Nova connects to the tools your team already uses—so you don't
                have to replace your systems.
              </p>
              <button
                type="button"
                ref={ctaRef}
                onClick={() => openExplorer()}
                data-testid="integrations-cta"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-nova-blue hover:gap-3 transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563FF] focus-visible:ring-offset-2 rounded-sm"
              >
                View all integrations
                <ArrowRight size={15} strokeWidth={2} />
              </button>
            </div>
          </Reveal>

          {/* Desktop / tablet radial ecosystem */}
          <Reveal delay={0.12} className="min-w-0 hidden lg:block">
            <div data-testid="integration-ecosystem" className="relative w-full aspect-[1/0.92]">
              <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                {[16, 24, 32].map((r) => (
                  <circle key={r} cx="50" cy="47" r={r} fill="none" stroke="#DBEAFE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ))}
                {NODES.map((n) => {
                  const mx = HUB.x + (n.x - HUB.x) * 0.5;
                  const my = HUB.y + (n.y - HUB.y) * 0.5;
                  const active = hovered === n.id;
                  return (
                    <g key={n.id}>
                      <line
                        x1={HUB.x} y1={HUB.y} x2={n.x} y2={n.y}
                        stroke="#2563EB"
                        strokeWidth={active ? 1.6 : 1}
                        strokeDasharray="0.1 3.2"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                        className="transition-opacity duration-300"
                        opacity={active ? 0.9 : 0.35}
                      />
                      <circle cx={mx} cy={my} r="0.8" fill="#2563EB" className="animate-connector-pulse" />
                    </g>
                  );
                })}
              </svg>

              <div
                data-testid="nova-hub"
                className="group absolute z-10 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${HUB.x}%`, top: `${HUB.y}%` }}
              >
                <span className="absolute inset-0 rounded-full bg-nova-blue/10 blur-2xl scale-125 animate-pulse-dot motion-reduce:animate-none" aria-hidden="true" />
                <span className="absolute -inset-4 rounded-full border border-[#DBEAFE]" aria-hidden="true" />
                <span className="absolute -inset-9 rounded-full border border-[#EFF6FF]" aria-hidden="true" />
                <div className="relative w-40 h-40 xl:w-48 xl:h-48 rounded-full bg-[#070D20] flex items-center justify-center shadow-[0_24px_60px_-12px_rgb(7,13,32,0.45)] transition-shadow duration-500 group-hover:shadow-[0_24px_70px_-8px_rgb(37,99,235,0.35)]">
                  <img
                    src="/nova-icon-official.png"
                    alt="Nova"
                    className="w-[66%] select-none"
                    draggable={false}
                    data-testid="nova-hub-logo"
                  />
                </div>
              </div>

              {NODES.map((n) => (
                <IntegrationCard key={n.id} node={n} hovered={hovered} setHovered={setHovered} onOpen={openExplorer} />
              ))}
            </div>
          </Reveal>

          {/* Mobile / stacked: hub + compact grid */}
          <Reveal delay={0.12} className="min-w-0 lg:hidden">
            <div data-testid="integration-ecosystem-mobile" className="flex flex-col items-center">
              <div className="relative">
                <span className="absolute -inset-4 rounded-full border border-[#DBEAFE]" aria-hidden="true" />
                <span className="absolute -inset-8 rounded-full border border-[#EFF6FF]" aria-hidden="true" />
                <div className="relative w-32 h-32 rounded-full bg-[#070D20] flex items-center justify-center shadow-[0_20px_50px_-12px_rgb(7,13,32,0.45)]">
                  <img src="/nova-icon-official.png" alt="Nova" className="w-[66%] select-none" draggable={false} />
                </div>
              </div>
              <div className="mt-12 grid grid-cols-2 min-[420px]:grid-cols-3 gap-3.5 w-full">
                {NODES.map((n) => (
                  <IntegrationCard key={n.id} node={n} hovered={hovered} setHovered={setHovered} onOpen={openExplorer} compact />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {explorerOpen && (
          <IntegrationExplorer
            open={explorerOpen}
            selectedId={selectedId}
            onClose={closeExplorer}
            onSelect={setSelectedId}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
